import { Accordion, Alert, Box, Button, Grid, Group, Loader, Menu, Paper, PasswordInput, Stack, Text, TextInput, Title, UnstyledButton, useMantineTheme } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import React, { useEffect, useState } from "react";
import { FaImage } from "react-icons/fa6";
import InputField, { InputFieldTypes } from "../components/input_field";
import { useApp } from "../contexts/app.context";
import { useLanguage } from "../contexts/language.context";
import { GET_COMPANY_DOMAINS, SAVE_COMPANY_DOMAIN, UPDATE_SELF_COMPANY } from "../services/companies";
import { GET_USER_DATA } from "../services/users";

export default function Appearance() {
    const [settings, setSettings] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingSave, setLoadingSave] = useState<boolean>(false);
    const [openImage, setOpenImage] = useState<boolean>(false);
    const [openIcon, setOpenIcon] = useState<boolean>(false);
    const [domains, setDomains] = useState<any[]>([]);

    const defaultDomain = domains.find(d => d.is_default);
    const notDefaultDomain = domains.find(d => !d.is_default);
    const theme = useMantineTheme();

    const { configs, setConfigs } = useApp();
    const { str } = useLanguage();
    const clipboard = useClipboard();

    const loadData = () => {
        setLoading(true);
        GET_USER_DATA()
            .then(({ company }) => {
                setSettings({ ...company?.appearance });
                setLoading(false);
            })
            .catch(err => {
                setLoading(false);
                notifications.show({ message: err.message, color: "red" })
            })
    }

    const loadDomains = () => {
        GET_COMPANY_DOMAINS()
            .then((res) => {
                setDomains(res);
            })
            .catch(err => {
                notifications.show({ message: err.message, color: "red" })
            })
    }

    const handleSaveDomain = (domain: string) => {
        setLoadingSave(true)
        SAVE_COMPANY_DOMAIN(domain)
            .then((res) => {
                loadDomains();
                notifications.show({ title: "Domain published", message: "Create records in your DNS manager", color: "green" })
            })
            .catch(err => {
                notifications.show({ message: err.message, color: "red" })
            })
            .finally(() => {
                setLoadingSave(false)

            })
    }

    const handleSaveWhitelabel = () => {
        UPDATE_SELF_COMPANY({ appearance: settings })
            .then(() => {
                notifications.show({ message: "Updated!", color: "green" })
            })
            .catch(err => {
                notifications.show({ title: "Ops.", message: err.message, color: 'red' })
            })
    }

    useEffect(() => {
        loadData()
        loadDomains()
    }, [])

    return <div style={{ position: 'relative' }}>
        {loading && <Loader />}
        <Title order={4}>{str("APPEARANCE.TITLE")}</Title>
        <Title mb="md" order={5} c="gray">{str("APPEARANCE.SUBTITLE")}</Title>
        <Paper p="md" mb="md" bg="dark.6">
            <Group>
                <Title style={{ flex: 1 }} order={5} c="gray">{defaultDomain?.url}</Title>
                <Button onClick={() => {
                    clipboard.copy(defaultDomain?.url);
                    notifications.show({ message: "Copied to clipboard", color: "yellow" })
                }} color="white" style={{ color: 'gray' }}>{str("COPY")}</Button>
            </Group>
        </Paper>
        <Accordion>
            <Accordion.Item mb="md" value="appearance" bg="dark.6">
                <Accordion.Control><Title order={4}>{str("APPEARANCE.APPEARANCE")}</Title></Accordion.Control>
                <Accordion.Panel>
                    <Grid>
                        <Grid.Col span={{ md: 6 }}>
                            <Paper shadow="xs" p="md">
                                <Box>
                                    {
                                        configs.image
                                            ? <img src={configs.image} height={80} width={200} color="gray" style={{ objectFit: 'contain' }} />
                                            : <FaImage size={60} color={theme.colors.gray[2]} />
                                    }
                                </Box>
                                <Menu opened={openImage} onOpen={() => setOpenImage(true)} onClose={() => setOpenImage(false)}>
                                    <Menu.Target>
                                        <UnstyledButton>
                                            <Button>{str("APPEARANCE.INSERT_LOGO")}</Button>
                                        </UnstyledButton>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <InputField
                                            name="image"
                                            title='Logo'
                                            value={configs.image}
                                            onChange={({ image }) => {
                                                setOpenIcon(false);
                                                setSettings((c: any) => ({ ...c, image }))
                                                if (image) setConfigs({ image })
                                            }}
                                            fieldType={InputFieldTypes.IMAGE}
                                        />
                                    </Menu.Dropdown>
                                </Menu>
                            </Paper>
                        </Grid.Col>
                        <Grid.Col span={{ md: 6 }}>
                            <Paper shadow="xs" p="md">
                                <Box>
                                    {
                                        configs.icon
                                            ? <img src={configs.icon} height={80} width={200} color="gray" style={{ objectFit: 'contain' }} />
                                            : <FaImage size={60} color={theme.colors.gray[2]} />
                                    }
                                </Box>
                                <Menu opened={openIcon} onOpen={() => setOpenIcon(true)} onClose={() => setOpenIcon(false)}>
                                    <Menu.Target>
                                        <UnstyledButton>
                                            <Button>Insert your icon</Button>
                                        </UnstyledButton>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <InputField
                                            name="icon"
                                            title='Icon'
                                            value={configs.icon}
                                            onChange={({ icon }) => {
                                                setOpenIcon(false);
                                                setSettings((c: any) => ({ ...c, icon }))
                                                if (icon) setConfigs({ icon })
                                            }}
                                            fieldType={InputFieldTypes.IMAGE}
                                        />
                                    </Menu.Dropdown>
                                </Menu>
                            </Paper>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <Paper p="md" mb="md" shadow="xs">
                                <Title order={4}>{str("APPEARANCE.CHANGE_COLOR_TITLE")}</Title>
                                <Text c="gray" size="md">{str("APPEARANCE.CHANGE_COLOR_SUBTITLE")}</Text>
                                <InputField
                                    mt="md"
                                    name="primary"
                                    fieldType={InputFieldTypes.COLOR}
                                    value={configs.primary}
                                    onChange={({ primary }) => {
                                        setConfigs({ primary });
                                        setSettings(s => ({
                                            ...s, colors: {
                                                ...s?.colors,
                                                primary
                                            }
                                        }));
                                    }}
                                />
                            </Paper>
                        </Grid.Col>
                        {/* <Grid.Col span={{ base: 12, md: 6 }}>
                            <Paper p="md" mb="md" shadow="xs">
                                <Title order={4}>Navbar Color</Title>
                                <Text c="gray" size="md">Choose your primary color that will be used in principal data.</Text>
                                <Group mt="md">
                                    <Box style={{
                                        height: 30,
                                        width: 30,
                                        borderRadius: 2,
                                        border: '1px solid #EFEFEF',
                                        background: configs.brand
                                    }}></Box>
                                    <InputField
                                        name="navbar"
                                        style={{flex: 1}}
                                        fieldType={InputFieldTypes.SELECT}
                                        clearable={false}
                                        searchable={false}
                                        options={[
                                            { label: "White", value: "white" },
                                            { label: "Black", value: "black" },
                                            { label: "Orange", value: "violet" },
                                            { label: "Yellow", value: "yellow" },
                                            { label: "Green", value: "green" },
                                            { label: "Blue", value: "blue" },
                                            { label: "Pink", value: "pink" },
                                        ]}
                                        value={configs.navbar || "white"}
                                        onChange={({navbar}) => {
                                            setConfigs({ navbar });
                                            setSettings(s => ({...s, colors: {
                                                ...s?.colors,
                                                navbar
                                            }}));
                                        }}
                                    />
                                </Group>
                            </Paper>
                        </Grid.Col> */}
                    </Grid>
                    {/* <Paper style={{ padding: 0, margin: '0 0 20px 0' }}>
                        <LoginPreview buttonColor={configs.primary} color={configs.brand} contrast={configs.contrast} />
                    </Paper> */}
                    <Group justify="flex-end">
                        <Button size="md" onClick={handleSaveWhitelabel} style={{ background: 'black' }}>{str("SAVE")}</Button>
                    </Group>
                </Accordion.Panel>
            </Accordion.Item>
            {/* <Accordion.Item mb="md" value="texts" bg="dark.6">
                <Accordion.Control><Title order={4}>Headlines & Descriptions</Title></Accordion.Control>
                <Accordion.Panel>
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 12 }}>
                            <Paper p="md" mb="md" shadow="xs">
                                <Title order={4}>Login/Sign-up Page Copy</Title>
                                <Text c="gray" size="md">Edit your texts on login page</Text>
                                <InputField
                                    mt="md"
                                    name="login_footer_one"
                                    title="Client Name"
                                    value={settings?.texts?.login_footer_one ?? "Ready to Experience Excellence?"}
                                    onChange={({ login_footer_one }) => setSettings(s => ({
                                        ...s, texts: {
                                            ...s?.texts,
                                            login_footer_one
                                        }
                                    }))}
                                />
                                <InputField
                                    mt="md"
                                    name="login_footer_two"
                                    title="Client Testimonial"
                                    fieldType={InputFieldTypes.TEXTAREA}
                                    value={settings?.texts?.login_footer_two ?? "Explore our array of top-tier services tailored to meet your needs. Sign up today and unlock the gateway to unparalleled solutions delivered by our dedicated team."}
                                    onChange={({ login_footer_two }) => setSettings(s => ({
                                        ...s, texts: {
                                            ...s?.texts,
                                            login_footer_two
                                        }
                                    }))}
                                />
                            </Paper>
                        </Grid.Col>
                    </Grid>
                    <Group justify="flex-end">
                        <Button size="md" onClick={handleSaveWhitelabel} style={{ background: 'black' }}>Save Description</Button>
                    </Group>
                </Accordion.Panel>
            </Accordion.Item> */}
            {/* <Accordion.Item mb="md" value="fields" bg="dark.6">
                <Accordion.Control><Title order={4}>Edit Client Signup Form</Title></Accordion.Control>
                <Accordion.Panel>
                    <FieldsPage />
                </Accordion.Panel>
            </Accordion.Item> */}
            <Accordion.Item mb="md" value="domains" bg="dark.6">
                <Accordion.Control><Title order={4}>{str("APPEARANCE.CONNECT_DOMAIN")}</Title></Accordion.Control>
                <Accordion.Panel>
                    <Stack>
                        {/* <Title mb="xs" order={4}>Connect your Domain</Title> */}
                        {/* <Text c="gray" mb="md">Paste your Domain.</Text> */}
                        <Group>
                            <InputField
                                name="url"
                                variant="filled"
                                color="gray"
                                placeholder="orders.example.com"
                                style={{ flex: 1 }}
                                value={notDefaultDomain?.url}
                                onChange={({ url }) => {
                                    setDomains(d => notDefaultDomain
                                        ? d.map(item => (item.id === notDefaultDomain?.id ? { ...item, url } : item))
                                        : [...d, { url }]
                                    )
                                }}
                            />
                            <Group justify="flex-end">
                                <Button size="md" loading={loadingSave || loading} onClick={() => handleSaveDomain(notDefaultDomain)} style={{ background: 'black' }}>Publish</Button>
                            </Group>
                        </Group>
                        {notDefaultDomain && <>
                            <Text>{str("APPEARANCE.CREATE_THIS_DNS")}</Text>
                            <Paper p="md">
                                <Grid>
                                    <Grid.Col style={{ textAlign: 'center' }} span={{ base: 12, xs: 2 }}>Type</Grid.Col>
                                    <Grid.Col style={{ textAlign: 'center' }} span={{ base: 12, xs: 5 }}>Record</Grid.Col>
                                    <Grid.Col style={{ textAlign: 'center' }} span={{ base: 12, xs: 5 }}>Target</Grid.Col>
                                    <Grid.Col style={{ textAlign: 'center' }} span={{ base: 12, xs: 2 }}><Paper shadow="xs" p="sm">CNAME</Paper></Grid.Col>
                                    <Grid.Col style={{ textAlign: 'center' }} span={{ base: 12, xs: 5 }}><Paper shadow="xs" p="sm">{notDefaultDomain?.url}</Paper></Grid.Col>
                                    <Grid.Col style={{ textAlign: 'center' }} span={{ base: 12, xs: 5 }}><Paper shadow="xs" p="sm">app.awaz.ai</Paper></Grid.Col>
                                    {/* <Grid.Col span={{ base: 12, xs: 2 }} style={{ padding: 0 }}>
                                    <Title c="gray" order={5} ta="center">OR</Title>
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, xs: 12 }} style={{ margin: 0, padding: 0 }}></Grid.Col>
                                <Grid.Col style={{ textAlign: 'center' }} span={{ base: 12, xs: 2 }}><Paper shadow="xs" p="sm">A</Paper></Grid.Col>
                                <Grid.Col style={{ textAlign: 'center' }} span={{ base: 12, xs: 5 }}><Paper shadow="xs" p="sm">{notDefaultDomain?.url}</Paper></Grid.Col>
                                <Grid.Col style={{ textAlign: 'center' }} span={{ base: 12, xs: 5 }}><Paper shadow="xs" p="sm">54.94.9.119</Paper></Grid.Col> */}
                                </Grid>
                            </Paper>
                        </>}
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    </div>
}


const LoginPreview = ({ buttonColor, color, contrast }) => <div style={{ height: '340px', display: 'flex', flexDirection: 'row', overflow: 'auto' }}>
    <div style={{ flex: 0.3, display: 'flex', flexDirection: 'column', background: color }}>
        <div style={{ flex: 1, padding: '20px 20px' }}>
            <Box>
                <Text color={contrast} size="md">Welcome Back</Text>
            </Box>
            <TextInput
                mt="xs"
                label="E-mail"
                name="email"
                size="xs"
                c={contrast}
            />
            <PasswordInput
                mt="xs"
                label="Password"
                name="password"
                size="xs"
                c={contrast}
            />
            <UnstyledButton style={{ width: '100%' }}>
                <Text style={{ textDecoration: 'undeline' }} size="xs" c="gray" ta="right">Forgot password?</Text>
            </UnstyledButton>
            <Button style={{ background: buttonColor }} className='draw-button' type="submit" mt="xl" size="xs" fullWidth>
                Sign In
            </Button>
        </div>
    </div>
    <div style={{ flex: 0.7, background: `url(/assets/login.png)`, height: '100%', backgroundSize: 'cover', backgroundRepeat: 'no-repeat' }}></div>
</div>